import { FC, useEffect, useState } from "react";
import {
    Segmented,
    Space,
    DealScore,
    SimpleCard,
    Spin,
} from "../../../design-system";

import {
    Project,
    ProjectAnalysis,
    ProjectAnalysisStatus,
    DealAnalysisOutput,
} from "../../../types/ProjectTypes";
import { getAnalysisOutput } from "../../../common/api/ProjectClient";
import { handleError } from "../../../common/ErrorHandling";
import { isSuperAdmin } from "../../../types/UserGroup";
import { useUserState } from "../../../common/UserContext";
import { Summary } from "./Summary";
import { AnalysisReport } from "./AnalysisReport";
import { AttachmentsList } from "./AttachmentsList";
import styles from "../ProjectView.module.less";

enum ViewTab {
    Summary = "Summary",
    Analysis = "Analysis",
    Attachments = "Attachments",
}

interface DealOutputProps {
    project: Project;
    analysis: ProjectAnalysis;
}

export const DealOutput: FC<DealOutputProps> = ({ project, analysis }) => {
    const [output, setOutput] = useState<DealAnalysisOutput>();

    useEffect(() => {
        let cancelled = false;
        getAnalysisOutput(project.id, analysis.version).then(
            (fetched) => {
                if (cancelled) return;
                setOutput(fetched as DealAnalysisOutput);
            },
            (error) => {
                if (cancelled) return;
                handleError(error);
            }
        );
        return () => {
            cancelled = true;
        };
    }, [project.id, analysis.version, analysis.recordUpdateDate]);

    if (!output) {
        return (
            <div className={styles.tabContent}>
                <Spin />
            </div>
        );
    }

    return <DealOutputBody project={project} analysis={analysis} output={output} />;
};

interface DealOutputBodyProps {
    project: Project;
    analysis: ProjectAnalysis;
    output: DealAnalysisOutput;
}

const DealOutputBody: FC<DealOutputBodyProps> = ({ project, analysis, output }) => {
    const { me } = useUserState();
    const isUserSuperAdmin = isSuperAdmin(me.roles || []);

    const hasSummary = !!output.summary;
    const showSummaryTab =
        hasSummary &&
        (analysis.status === ProjectAnalysisStatus.COMPLETED || isUserSuperAdmin);
    const showAttachmentsTab = (analysis.attachments?.length ?? 0) > 0;

    const segmentedOptions = [
        ...(showSummaryTab ? [{ label: ViewTab.Summary, value: ViewTab.Summary }] : []),
        { label: ViewTab.Analysis, value: ViewTab.Analysis },
        ...(showAttachmentsTab ? [{ label: ViewTab.Attachments, value: ViewTab.Attachments }] : []),
    ];

    const defaultTab = showSummaryTab ? ViewTab.Summary : ViewTab.Analysis;
    const [activeTab, setActiveTab] = useState<ViewTab>(defaultTab);

    return (
        <>
            <div className={styles.tabRow}>
                {segmentedOptions.length > 1 && (
                    <>
                        <Segmented
                            options={segmentedOptions}
                            value={activeTab}
                            onChange={(value) => setActiveTab(value as ViewTab)}
                        />
                        <div style={{ flex: 1 }} />
                    </>
                )}
                {output.dealInfo &&
                    output.dealInfo.orderScore > 0 &&
                    (analysis.status === ProjectAnalysisStatus.COMPLETED || isUserSuperAdmin) && (
                        <DealScore
                            orderFormId={project.orderForm?.id}
                            score={output.dealInfo.orderScore}
                            optimizedScore={output.dealInfo.optimizedScore}
                        />
                    )}
            </div>

            {activeTab === ViewTab.Summary && (
                <div className={styles.tabContent}>
                    <Space direction="vertical" size="large" fullWidth>
                        <Summary output={output} />
                    </Space>
                </div>
            )}

            {activeTab === ViewTab.Analysis && (
                <div className={styles.tabContent}>
                    <SimpleCard header="Deal analysis" headerType="primary">
                        <SimpleCard type="white">
                            <AnalysisReport
                                projectId={project.id}
                                analysisVersion={analysis.version}
                                output={output}
                                userFeedback={analysis.userFeedback}
                            />
                        </SimpleCard>
                    </SimpleCard>
                </div>
            )}

            {activeTab === ViewTab.Attachments && (
                <div className={styles.tabContent}>
                    <AttachmentsList project={project} analysis={analysis} />
                </div>
            )}
        </>
    );
};
